import React, { useCallback, useMemo, useState } from 'react'
import { gql, useMutation, useQuery, useApolloClient } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import SaveIcon from '@mui/icons-material/Save'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import ReactQuill, { Quill } from 'react-quill'
import ImageResize from 'quill-image-resize-module-react'
import ImageUploader from 'quill-image-uploader'
import StyledModal from 'components/Modal'
import {
  Button,
  Box,
  Fab,
  Stack,
  Typography,
  useTheme,
  CircularProgress,
} from '@mui/material'
import Loader from 'components/Loader'

const Block = Quill.import('blots/block')
Block.tagName = 'div'

Quill.register('modules/imageResize', ImageResize)
Quill.register('modules/imageUploader', ImageUploader)

export const UPLOAD_NOTES_IMAGE = gql`
  mutation UPLOAD_NOTES_IMAGE($file: Upload!) {
    url: uploadNotesImage(file: $file)
  }
`

const siteNotesSchema = Yup.object({
  html: Yup.string(),
})

const toolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  ['blockquote', 'code-block'],
  ['link', 'image', 'video', 'formula'],

  [{ header: 1 }, { header: 2 }], // custom button values
  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
  [{ direction: 'rtl' }], // text direction

  [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
  [{ header: [1, 2, 3, 4, 5, 6, false] }],

  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ font: [] }],
  [{ align: [] }],

  ['clean'], // remove formatting button
]

const modules = {
  toolbar: toolbarOptions,
  imageResize: {
    parchment: Quill.import('parchment'),
    modules: ['Resize', 'DisplaySize'],
  },
}

const FIND_SITE_NOTE = gql`
  query FIND_SITE_NOTE($filter: JSON!) {
    siteNotes: findSiteNotes(filter: $filter) {
      id
      html
    }
  }
`

const UPDATE_SITE_NOTE = gql`
  mutation UPDATE_SITE_NOTE($id: ID, $siteNote: SiteNoteInput!) {
    updateSiteNote(id: $id, siteNote: $siteNote) {
      id
      html
    }
  }
`

const CREATE_SITE_NOTE = gql`
  mutation CREATE_SITE_NOTE($siteNote: SiteNoteInput!) {
    createSiteNote(siteNote: $siteNote) {
      id
      html
    }
  }
`

type SiteNotesFormPropTypes = {
  html: string
  onCancel: () => void
  id: string
  siteId: string
  refetch: () => void
}

type SiteNotesDataType = Yup.InferType<typeof siteNotesSchema>

function SiteNoteForm({
  html,
  onCancel,
  id,
  siteId,
  refetch,
}: SiteNotesFormPropTypes) {
  const { handleSubmit, control } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { html },
    resolver: yupResolver(siteNotesSchema),
  })

  const client = useApolloClient()

  const onCompleted = () => {
    refetch()
    onCancel()
  }

  const [updateSiteNote, { loading: updatingSiteNote }] = useMutation(
    UPDATE_SITE_NOTE,
    { onCompleted }
  )
  const [createSiteNote, { loading: creatingSiteNote }] = useMutation(
    CREATE_SITE_NOTE,
    { onCompleted }
  )

  const onSubmit = (data: SiteNotesDataType) => {
    const siteNote = { site: siteId, ...data }

    if (id) {
      updateSiteNote({ variables: { id, siteNote } })
    } else {
      createSiteNote({ variables: { siteNote } })
    }
  }

  const uploadHandler = useCallback(
    async (file: File) => {
      const { data } = await client.mutate({
        mutation: UPLOAD_NOTES_IMAGE,
        variables: { file },
      })

      return data.url
    },
    [client]
  )

  //WARNING: Removing useMemo may cause Quill to vanish like your weekend plans — without a trace.
  const quillModules = useMemo(
    () => ({
      ...modules,
      imageUploader: {
        upload: uploadHandler,
      },
    }),
    [uploadHandler]
  )

  return (
    <Box
      component="form"
      sx={{ position: 'relative', height: '100%' }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Controller
        render={({ field }) => (
          <ReactQuill
            id="listingNoteEditor"
            {...field}
            theme="snow"
            modules={quillModules}
            style={{
              height: 510,
            }}
          />
        )}
        name="html"
        control={control}
      />
      <Fab
        disabled={creatingSiteNote || updatingSiteNote}
        type="submit"
        variant="extended"
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
        }}
        color="primary"
      >
        {creatingSiteNote || updatingSiteNote ? (
          <CircularProgress sx={{ mr: 1 }} color="inherit" size={24} />
        ) : (
          <SaveIcon sx={{ mr: 1 }} />
        )}
        {`Update`}
      </Fab>
    </Box>
  )
}

type EditSiteNotePropTypes = {
  id: string
  onCancel: () => void
}

function EditSiteNote({ id, onCancel }: EditSiteNotePropTypes) {
  const { loading, error, data, refetch } = useQuery(FIND_SITE_NOTE, {
    variables: { filter: { site: id } },
  })

  if (loading) return <Loader />
  if (error) return <p>error...</p>

  return (
    <SiteNoteForm
      html={data.siteNotes[0]?.html || ''}
      onCancel={onCancel}
      id={data.siteNotes[0]?.id}
      siteId={id}
      refetch={refetch}
    />
  )
}

function SiteNotes({ id, name }: { id: string; name: string }) {
  const [show, setShow] = useState(false)

  const theme = useTheme()

  const handleClose = () => setShow(false)
  const handleShow = () => setShow(true)

  return (
    <React.Fragment>
      <Button
        size="small"
        variant="contained"
        sx={{ height: 'fit-content' }}
        onClick={handleShow}
      >
        Details
      </Button>
      <StyledModal
        open={show}
        width={`75%`}
        maxWidth={1200}
        sx={{ height: '100%', maxHeight: 660 }}
      >
        <Stack spacing={1} sx={{ height: '100%' }}>
          <Stack
            direction={`row`}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography
              sx={{
                ...theme.typography.body1,
                color: theme.palette.primary.light,
              }}
            >
              <b>{name}</b>
            </Typography>
            <CancelIcon
              onClick={handleClose}
              sx={{ cursor: 'pointer', color: theme.palette.error.main }}
            />
          </Stack>
          <Box sx={{ flex: 1, position: 'relative' }}>
            <EditSiteNote id={id} onCancel={handleClose} />
          </Box>
        </Stack>
      </StyledModal>
    </React.Fragment>
  )
}

export default SiteNotes
